<template>
  <div class="PmDefaultViewsPure">
    <div v-if="hasNotifications" class="PmDefaultViewsPure-notifications">
      <PmErrorNotificationPure
        v-if="isNotificationErrorVisible"
        :message="errorMessage || state.meta.value?.errorMessage"
        :details="errorDetails"
      />
    </div>

    <form class="PmDefaultViewsPure-form" @submit.prevent="submit">
      <div class="PmDefaultViewsPure-inputs">
        <PmDropdownPure
          v-model:value="selectedViewIdMobile"
          label="Mobil"
          class="PmDefaultViewsPure-input"
          :options="views"
        />

        <PmDropdownPure
          v-model:value="selectedViewIdDesktop"
          label="Desktop"
          class="PmDefaultViewsPure-input"
          :options="views"
        />
      </div>

      <div class="PmDefaultViewsPure-control">
        <PmButtonPure
          :icon="ICONS.SAVE"
          label="Speichern"
          variant="primary"
          class="PmDefaultViewsPure-submit"
          :loading="isLoading"
          type="submit"
          :disabled="!hasFormChanges"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { toRef, computed, ref } from 'vue'
import { isEqual } from 'lodash-es'
import { ICONS, type Icon } from '@/constants/icons'
import { useXState, type StatePathOrValue } from '@/composition/useXState5'
import { PmDefaultViewsPureState } from '@/components/PmDefaultViews/PmDefaultViewsPureState'
import PmDropdownPure from '@/components/basics/PmDropdownPure.vue'
import PmAppNotificationPure from '@/components/basics/PmAppNotification/PmAppNotificationPure.vue'
import PmErrorNotificationPure from '@/components/basics/PmErrorNotificationPure.vue'
import PmButtonPure from '@/components/basics/PmButtonPure.vue'

export type Props = {
  state?: StatePathOrValue<typeof PmDefaultViewsPureState>
  views?: ViewNormalized[]
  errorMessage?: string
  errorDetails?: string[]
  initialFormData: {
    selectedViewIdMobile?: number
    selectedViewIdDesktop?: number
  }
}

type ViewNormalized = {
  id: number
  label: string
  icon?: Icon
  iconTitle?: string
}

const props = withDefaults(defineProps<Props>(), {})

export type Emits = {
  submit: [
    {
      viewIdMobile: number | undefined
      viewIdDesktop: number | undefined
    },
  ]
}

const emit = defineEmits<Emits>()

const state = useXState(PmDefaultViewsPureState, {
  syncStateWith: toRef(props, 'state'),
})

const selectedViewIdMobile = ref()
const selectedViewIdDesktop = ref()

const isLoading = computed(() => {
  if (state.value.snapshot.matches('saving')) return true

  return false
})

const isNotificationErrorVisible = computed(() => {
  return state.value.meta.value?.error ? true : false
})

const hasNotifications = computed(() => {
  if (isNotificationErrorVisible.value) return true

  return false
})

const formData = computed(() => {
  return {
    selectedViewIdMobile: selectedViewIdMobile,
    selectedViewIdDesktop: selectedViewIdDesktop,
  }
})

const hasFormChanges = computed(() => {
  return !isEqual(formData.value, props.initialFormData)
})

selectedViewIdMobile.value = props.initialFormData.selectedViewIdMobile
selectedViewIdDesktop.value = props.initialFormData.selectedViewIdDesktop

function submit() {
  emit('submit', {
    viewIdMobile: selectedViewIdMobile.value,
    viewIdDesktop: selectedViewIdDesktop.value,
  })
}
</script>

<style lang="scss">
.PmDefaultViewsPure {
  $block: &;

  &-title {
    @include typography.h3;

    margin-bottom: 1em;
  }

  &-notifications {
    margin-bottom: 28px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &-inputs {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 20px;

    @include mixin.media('<=mobile') {
      grid-template-columns: 1fr;
    }
  }

  &-input {
    // Empty
    flex-grow: 1;
  }

  &-control {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
